import type { TelemetryClient } from "../core/client.js";
import type { TelemetryFramework } from "../core/types.js";
import {
  createModuleFromRegistry,
  type TelemetryModule,
} from "../di/define-module.js";
import type { TelemetryBindingsSource } from "../di/bindings-source.js";
import { TELEMETRY_CLIENT_TOKEN, TELEMETRY_SERVER_TOKEN } from "../di/tokens.js";
import type { TelemetryFrameworkAdapter } from "../schema/di.js";
import { parseTelemetryDiSchema } from "../schema/di.js";
import { normalizeSetupInput } from "./normalize.js";
import type { PlatformBuildContext } from "./platform-context.js";

function platformIdToAdapter(id: TelemetryFramework): TelemetryFrameworkAdapter {
  return id === "browser" ? "generic" : id;
}

export function createModuleShell<TModule extends TelemetryModule = TelemetryModule>(
  ctx: PlatformBuildContext<TModule>,
): TModule {
  const di = normalizeSetupInput(ctx.plan.moduleInput);
  const schema = parseTelemetryDiSchema({
    ...di,
    framework: platformIdToAdapter(ctx.plan.platform.id),
  });

  const instances = new Map<string | symbol, TelemetryClient>();

  const syncInstances = (): void => {
    const client = ctx.getClient();
    const server = ctx.getServer();
    instances.clear();
    if (client) {
      instances.set(TELEMETRY_CLIENT_TOKEN, client);
      instances.set(schema.injection.clientKey, client);
    }
    if (server) {
      instances.set(TELEMETRY_SERVER_TOKEN, server);
      instances.set(schema.injection.serverKey, server);
    }
  };

  const registry: TelemetryBindingsSource = {
    schema,
    getClient: () => {
      syncInstances();
      return (
        instances.get(schema.injection.clientKey) ??
        instances.get(TELEMETRY_CLIENT_TOKEN)
      );
    },
    getServer: () => {
      syncInstances();
      return (
        instances.get(schema.injection.serverKey) ??
        instances.get(TELEMETRY_SERVER_TOKEN)
      );
    },
    getBindings: () => {
      syncInstances();
      return instances;
    },
    resolve: (token: string | symbol = TELEMETRY_CLIENT_TOKEN) => {
      syncInstances();
      return instances.get(token);
    },
  };

  return {
    ...createModuleFromRegistry(registry),
  } as TModule;
}
